/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Dropzone from "react-dropzone";
import {
    FiUpload,
    FiDownload,
    FiFileText,
    FiAlertTriangle,
    FiArrowLeft,
    FiArrowRight,
} from "react-icons/fi";

import Notification from "../../../modules/Notifications";
import Confirm from "../../../modules/AlertConfirm";
import api from "../../../service/api";
import {
    parseSheet,
    buildPlan,
    applyPlan,
    downloadTemplate,
    downloadReport,
} from "../../../service/productsImportExport";

import {
    Dropzone as DropzoneBox,
    Summary,
    TemplateCard,
    PreviewTable,
    ActionBadge,
    FilterBar,
    ProgressBar,
    Stepper,
    StepItem,
    StepIntro,
    WarningCallout,
} from "./styles";

const MAX_SIZE_BYTES = 5 * 1024 * 1024;

const STEPS = [
    { id: 1, label: "Arquivo" },
    { id: 2, label: "Revisão" },
    { id: 3, label: "Confirmação" },
    { id: 4, label: "Processamento" },
];

const Emoji = ({ label, children }) => (
    <span role="img" aria-label={label}>
        {children}
    </span>
);

const ImportProductsModal = ({ show, onClose, onImported }) => {
    const [step, setStep] = useState(1);
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [rawRows, setRawRows] = useState([]);
    const [plan, setPlan] = useState([]);
    const [filter, setFilter] = useState("all");
    const [onlyWarnings, setOnlyWarnings] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [results, setResults] = useState(null);
    const abortRef = useRef(false);
    const runningRef = useRef(false);

    const reset = useCallback(() => {
        setStep(1);
        setFile(null);
        setParsing(false);
        setRawRows([]);
        setPlan([]);
        setFilter("all");
        setOnlyWarnings(false);
        setProgress({ done: 0, total: 0 });
        setResults(null);
        abortRef.current = false;
        runningRef.current = false;
    }, []);

    const handleClose = useCallback(() => {
        if (runningRef.current) {
            Confirm(
                "Importação em andamento",
                "Cancelar agora? Os produtos já gravados serão mantidos.",
                () => {
                    abortRef.current = true;
                }
            );
            return;
        }
        reset();
        onClose();
    }, [reset, onClose]);

    const handleApply = useCallback(async () => {
        if (runningRef.current) return;
        runningRef.current = true;
        abortRef.current = false;
        setStep(4);
        setProgress({ done: 0, total: plan.length });
        setResults(null);

        try {
            const res = await applyPlan(
                plan,
                (p) => setProgress(p),
                undefined,
                () => abortRef.current
            );
            setResults(res);
            const created = res.filter((r) => r.status === "created").length;
            const updated = res.filter((r) => r.status === "updated").length;
            Notification(
                "success",
                `Importação finalizada: ${created} criados, ${updated} atualizados`
            );
            if (onImported) onImported();
        } catch (err) {
            Notification("error", err.message || "Falha na importação");
        } finally {
            runningRef.current = false;
        }
    }, [plan, onImported]);

    const prevShowRef = useRef(show);
    useEffect(() => {
        if (show && !prevShowRef.current) {
            reset();
        }
        prevShowRef.current = show;
    }, [show, reset]);

    useEffect(() => {
        if (step !== 4 || results !== null) return undefined;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [step, results]);

    const handleDrop = (accepted) => {
        const picked = accepted[0];
        if (!picked) return;
        if (!/\.xlsx$/i.test(picked.name)) {
            Notification("error", "Arquivo precisa ter extensão .xlsx");
            return;
        }
        if (picked.size > MAX_SIZE_BYTES) {
            Notification("error", "Arquivo excede o limite de 5 MB");
            return;
        }
        setFile(picked);
    };

    const handleAdvanceToPreview = async () => {
        if (!file) return;
        setParsing(true);
        try {
            const [
                { rows, warnings: sheetWarnings },
                produtosRes,
                linhasRes,
                aplicacoesRes,
            ] = await Promise.all([
                parseSheet(file),
                api.get("/produtos?_limit=-1"),
                api.get("/linhas?_limit=-1"),
                api.get("/aplicacaos?_limit=-1"),
            ]);
            sheetWarnings.forEach((w) => Notification("warning", w));
            const planResult = buildPlan(
                rows,
                produtosRes.data,
                linhasRes.data,
                aplicacoesRes.data
            );
            setRawRows(rows);
            setPlan(planResult);
            setStep(2);
        } catch (err) {
            Notification("error", err.message || "Falha ao ler a planilha");
        } finally {
            setParsing(false);
        }
    };

    const counts = useMemo(
        () =>
            plan.reduce(
                (acc, item) => {
                    if (item.action === "create") acc.create += 1;
                    else if (item.action === "update" || item.action === "update-pending") {
                        acc.update += 1;
                    } else if (item.action === "skip-empty-name") acc.skip += 1;
                    if (item.warnings.length > 0) acc.warnings += 1;
                    return acc;
                },
                { create: 0, update: 0, skip: 0, warnings: 0 }
            ),
        [plan]
    );

    const filteredPlan = useMemo(
        () =>
            plan
                .filter((item) => {
                    if (filter === "create") return item.action === "create";
                    if (filter === "update")
                        return ["update", "update-pending"].includes(item.action);
                    if (filter === "skip") return item.action === "skip-empty-name";
                    return true;
                })
                .filter((item) => (onlyWarnings ? item.warnings.length > 0 : true)),
        [plan, filter, onlyWarnings]
    );

    const actionMeta = (item) => {
        if (item.action === "create") return { label: "Criar", tone: "create", icon: "🆕" };
        if (item.action === "update")
            return { label: `Atualizar #${item.matchedProductId}`, tone: "update", icon: "✏️" };
        if (item.action === "update-pending")
            return { label: "Atualizar (criado agora)", tone: "update", icon: "✏️" };
        if (item.action === "skip-empty-name")
            return { label: "Ignorar", tone: "skip", icon: "⚠️" };
        return { label: "Erro", tone: "error", icon: "🚫" };
    };

    const progressPct =
        progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>Importar produtos</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Stepper aria-label="Progresso da importação">
                    {STEPS.map((s, idx) => {
                        const state =
                            s.id < step ? "is-done" : s.id === step ? "is-active" : "";
                        return (
                            <StepItem key={s.id} className={state}>
                                <span className="step-circle">{s.id}</span>
                                <span className="step-label">{s.label}</span>
                                {idx < STEPS.length - 1 && (
                                    <span className="step-connector" />
                                )}
                            </StepItem>
                        );
                    })}
                </Stepper>

                <div style={{ marginTop: "2rem" }}>
                    {step === 1 && (
                        <>
                            <StepIntro>
                                <p>
                                    Baixe o template, preencha as colunas{" "}
                                    <strong>Nome</strong>, <strong>Linha</strong> e{" "}
                                    <strong>Aplicações</strong>, depois solte o arquivo
                                    abaixo.
                                </p>
                            </StepIntro>

                            <TemplateCard>
                                <div className="template-info">
                                    <span className="template-icon">
                                        <FiFileText />
                                    </span>
                                    <span className="template-text">
                                        <span className="template-title">
                                            Template de importação
                                        </span>
                                        <span className="template-subtitle">
                                            Planilha .xlsx com as colunas obrigatórias
                                        </span>
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className="btn-ntn btn-ntn--secondary"
                                    onClick={downloadTemplate}
                                >
                                    <FiDownload />
                                    <span>Baixar template</span>
                                </button>
                            </TemplateCard>

                            <Dropzone
                                onDrop={handleDrop}
                                accept=".xlsx"
                                multiple={false}
                            >
                                {({ getRootProps, getInputProps, isDragActive }) => (
                                    <DropzoneBox
                                        {...getRootProps()}
                                        className={isDragActive ? "active" : ""}
                                    >
                                        <input {...getInputProps()} />
                                        <span className="dropzone-icon">
                                            <FiUpload />
                                        </span>
                                        {file ? (
                                            <>
                                                <span className="dropzone-filename">
                                                    <FiFileText /> {file.name}
                                                </span>
                                                <span className="dropzone-hint">
                                                    Clique ou solte outro arquivo para
                                                    substituir
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <div>
                                                    Arraste o arquivo <code>.xlsx</code> aqui
                                                </div>
                                                <span className="dropzone-hint">
                                                    ou clique para selecionar (máx. 5 MB)
                                                </span>
                                            </>
                                        )}
                                    </DropzoneBox>
                                )}
                            </Dropzone>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <Summary>
                                <span>
                                    <Emoji label="arquivo">📄</Emoji>{" "}
                                    {file && file.name}
                                </span>
                                <span>
                                    <Emoji label="criar">🆕</Emoji> Criar: {counts.create}
                                </span>
                                <span>
                                    <Emoji label="atualizar">✏️</Emoji> Atualizar:{" "}
                                    {counts.update}
                                </span>
                                <span>
                                    <Emoji label="ignorar">⚠️</Emoji> Ignorar: {counts.skip}
                                </span>
                                <span>
                                    <Emoji label="avisos">❗</Emoji> Avisos:{" "}
                                    {counts.warnings}
                                </span>
                            </Summary>
                            <FilterBar>
                                <select
                                    className="form-control"
                                    style={{ maxWidth: 240 }}
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    aria-label="Filtrar por ação"
                                >
                                    <option value="all">Todos</option>
                                    <option value="create">Criar</option>
                                    <option value="update">Atualizar</option>
                                    <option value="skip">Ignorar</option>
                                </select>
                                <label
                                    className="d-flex align-items-center"
                                    style={{ gap: 8, margin: 0 }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={onlyWarnings}
                                        onChange={(e) =>
                                            setOnlyWarnings(e.target.checked)
                                        }
                                    />
                                    Só com avisos
                                </label>
                            </FilterBar>
                            <PreviewTable>
                                <table>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 56 }}>#</th>
                                            <th>Nome</th>
                                            <th>Linha</th>
                                            <th style={{ width: 92 }}>Aplicações</th>
                                            <th style={{ width: 220 }}>Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredPlan.map((item) => {
                                            const raw = rawRows.find(
                                                (r) => r.rowNumber === item.rowNumber
                                            );
                                            const meta = actionMeta(item);
                                            const hasWarn = item.warnings.length > 0;
                                            return (
                                                <React.Fragment key={item.rowNumber}>
                                                    <tr
                                                        className={
                                                            hasWarn
                                                                ? "has-warning"
                                                                : undefined
                                                        }
                                                    >
                                                        <td>{item.rowNumber}</td>
                                                        <td>{raw && raw.Nome}</td>
                                                        <td>{raw && raw.Linha}</td>
                                                        <td>
                                                            {item.payload &&
                                                                Array.isArray(
                                                                    item.payload.aplicacaos
                                                                ) &&
                                                                item.payload.aplicacaos.length}
                                                        </td>
                                                        <td>
                                                            <ActionBadge $tone={meta.tone}>
                                                                <span>{meta.icon}</span>
                                                                {meta.label}
                                                            </ActionBadge>
                                                        </td>
                                                    </tr>
                                                    {hasWarn && (
                                                        <tr className="warning-details">
                                                            <td colSpan={5}>
                                                                <ul>
                                                                    {item.warnings.map(
                                                                        (w, i) => (
                                                                            <li key={i}>
                                                                                <FiAlertTriangle />
                                                                                <span>{w}</span>
                                                                            </li>
                                                                        )
                                                                    )}
                                                                </ul>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </PreviewTable>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <Summary>
                                <span>
                                    <Emoji label="criar">🆕</Emoji> Criar: {counts.create}
                                </span>
                                <span>
                                    <Emoji label="atualizar">✏️</Emoji> Atualizar:{" "}
                                    {counts.update}
                                </span>
                                <span>
                                    <Emoji label="ignorar">⚠️</Emoji> Ignorar: {counts.skip}
                                </span>
                            </Summary>
                            <StepIntro>
                                <p>Confirme abaixo para aplicar as alterações na base.</p>
                            </StepIntro>
                            <WarningCallout>
                                <div className="callout-title">
                                    <FiAlertTriangle /> Atenção
                                </div>
                                <ul>
                                    <li>
                                        A atualização toca somente <strong>Linha</strong> e{" "}
                                        <strong>Aplicações</strong>. Imagem, PDFs
                                        (CDOC1-4), vídeo e relações de cliente/exclusivo
                                        são preservados.
                                    </li>
                                    <li>
                                        Produtos novos serão criados com{" "}
                                        <code>exclusivo: false</code>.
                                    </li>
                                    <li>Esta operação não pode ser desfeita.</li>
                                </ul>
                            </WarningCallout>
                        </>
                    )}

                    {step === 4 && (
                        <>
                            {results === null ? (
                                <div>
                                    <StepIntro>
                                        <p>
                                            Aplicando alterações...{" "}
                                            <strong>
                                                {progress.done}/{progress.total} ({progressPct}%)
                                            </strong>
                                        </p>
                                    </StepIntro>
                                    <ProgressBar>
                                        <div style={{ width: `${progressPct}%` }} />
                                    </ProgressBar>
                                </div>
                            ) : (
                                <>
                                    <Summary>
                                        <span>
                                            <Emoji label="criados">🆕</Emoji> Criados:{" "}
                                            {results.filter((r) => r.status === "created").length}
                                        </span>
                                        <span>
                                            <Emoji label="atualizados">✏️</Emoji> Atualizados:{" "}
                                            {results.filter((r) => r.status === "updated").length}
                                        </span>
                                        <span>
                                            <Emoji label="ignorados">⚠️</Emoji> Ignorados:{" "}
                                            {results.filter((r) => r.status === "skipped").length}
                                        </span>
                                        <span>
                                            <Emoji label="falhas">🚫</Emoji> Falhas:{" "}
                                            {results.filter((r) => r.status === "failed").length}
                                        </span>
                                        {results.some((r) => r.status === "aborted") && (
                                            <span>
                                                <Emoji label="cancelados">⛔</Emoji>{" "}
                                                Cancelados:{" "}
                                                {results.filter((r) => r.status === "aborted").length}
                                            </span>
                                        )}
                                    </Summary>
                                    <button
                                        type="button"
                                        className="btn-ntn btn-ntn--primary"
                                        onClick={() =>
                                            downloadReport(
                                                rawRows,
                                                results,
                                                `relatorio-importacao-${new Date()
                                                    .toISOString()
                                                    .slice(0, 10)}.xlsx`
                                            )
                                        }
                                    >
                                        <FiDownload />
                                        <span>Baixar relatório (.xlsx)</span>
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                {step === 1 && (
                    <>
                        <button
                            type="button"
                            className="btn-ntn btn-ntn--ghost"
                            onClick={handleClose}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn-ntn btn-ntn--primary"
                            disabled={!file || parsing}
                            onClick={handleAdvanceToPreview}
                        >
                            {parsing ? (
                                <span>Lendo planilha...</span>
                            ) : (
                                <>
                                    <span>Avançar</span>
                                    <FiArrowRight />
                                </>
                            )}
                        </button>
                    </>
                )}
                {step === 2 && (
                    <>
                        <button
                            type="button"
                            className="btn-ntn btn-ntn--ghost"
                            onClick={() => setStep(1)}
                        >
                            <FiArrowLeft />
                            <span>Voltar</span>
                        </button>
                        <button
                            type="button"
                            className="btn-ntn btn-ntn--primary"
                            onClick={() => setStep(3)}
                        >
                            <span>Confirmar importação</span>
                            <FiArrowRight />
                        </button>
                    </>
                )}
                {step === 3 && (
                    <>
                        <button
                            type="button"
                            className="btn-ntn btn-ntn--ghost"
                            onClick={() => setStep(2)}
                        >
                            <FiArrowLeft />
                            <span>Voltar</span>
                        </button>
                        <button
                            type="button"
                            className="btn-ntn btn-ntn--danger"
                            onClick={handleApply}
                        >
                            <FiUpload />
                            <span>Importar agora</span>
                        </button>
                    </>
                )}
                {step === 4 && results === null && (
                    <button
                        type="button"
                        className="btn-ntn btn-ntn--ghost"
                        onClick={handleClose}
                    >
                        Cancelar
                    </button>
                )}
                {step === 4 && results !== null && (
                    <button
                        type="button"
                        className="btn-ntn btn-ntn--primary"
                        onClick={handleClose}
                    >
                        Fechar
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ImportProductsModal;
